import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, TablePagination, makeStyles, Theme } from '@material-ui/core'
import { toast } from 'react-toastify'

import { useLeads, LeadsFilters } from '../../../hooks/Outreach/useLeads'
import { useLeadTour } from '../../../hooks/Outreach/useLeadTour'
import { Lead } from '../../../models/Outreach/types'
import { AddWalkInLeadDialog } from './AddWalkInLeadDialog'
import { usePersonFile } from '../PersonFile/PersonFileContext'
import { InviteToApplyDialog } from './InviteToApplyDialog'
import { ScheduleTourDialog } from './ScheduleTourDialog'
import {
    LeadsRosterHeader,
    LeadsRosterScope,
} from './components/LeadsRosterHeader'
import {
    PipelineToolbar,
    ApplicationStatusFilter,
    ScreeningStatusFilter,
} from './components/PipelineToolbar'
import { StageSwitcher } from './components/StageSwitcher'
import { StageTable } from './components/StageTable'
import { SendLeaseOfferDialog } from './SendLeaseOfferDialog'
import { mapLeadToPipelineRecord } from './mapLeadToPipelineRecord'
import {
    DEFAULT_PIPELINE_TABS,
    remapPipelineCountsToDemoTabs,
    tabIdToColumnStage,
} from './pipelineTabs'
import { PIPELINE_STAGE_LABEL, PipelineRecord, PipelineStage } from './types'
import {
    EMPTY_SOURCE_COUNTS,
    LeadSourceCounts,
    usePipelineCounts,
} from '../hooks/usePipelineCounts'
import { axiosInstance } from '../../../helpers'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    stickyChrome: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        paddingBottom: theme.spacing(0.5),
        backgroundColor: theme.palette.background.default,
        boxShadow: `0 1px 0 ${theme.palette.divider}`,
    },
    tableSlot: {
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    paginationSlot: {
        flexShrink: 0,
        backgroundColor: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

interface LeadsPipelineHomeProps {
    seasonId: number | null | undefined
    apartmentId: number | undefined
    readOnly?: boolean
}

function resolvePipelineTabFilter(
    tabId: string,
    isSearchActive: boolean,
    leadsScope: LeadsRosterScope,
): string | undefined {
    // Global search (non-Leads tabs): match across the season.
    if (isSearchActive && tabId !== 'leads') {
        return undefined
    }
    if (tabId === 'leads') {
        return leadsScope === 'everyone' ? 'everyone' : 'leads'
    }
    return tabId
}

function sumCounts(counts: Record<string, number> | null | undefined): number {
    if (!counts) {
        return 0
    }
    return Object.values(counts).reduce((sum, n) => sum + (n || 0), 0)
}

export const LeadsPipelineHome: React.FC<LeadsPipelineHomeProps> = ({
    seasonId,
    apartmentId,
    readOnly = false,
}) => {
    const classes = useStyles()
    // Demo: fixed ops tabs (ignore season pipeline-config tab derivation).
    const pipelineTabs = DEFAULT_PIPELINE_TABS
    const [activeTabId, setActiveTabId] = useState(pipelineTabs[0].id)
    const [leadsScope, setLeadsScope] = useState<LeadsRosterScope>('everyone')
    const [selectedSources, setSelectedSources] = useState<string[]>([])
    const [query, setQuery] = useState('')
    const [
        applicationStatusFilter,
        setApplicationStatusFilter,
    ] = useState<ApplicationStatusFilter>('all')
    const [
        screeningStatusFilter,
        setScreeningStatusFilter,
    ] = useState<ScreeningStatusFilter>('all')
    const [addLeadOpen, setAddLeadOpen] = useState(false)
    const [inviteLead, setInviteLead] = useState<Lead | null>(null)
    const [tourLead, setTourLead] = useState<Lead | null>(null)
    const [editTourId, setEditTourId] = useState<number | null>(null)
    const [offerRecord, setOfferRecord] = useState<PipelineRecord | null>(null)
    const [remindingLeadId, setRemindingLeadId] = useState<number | null>(null)
    const [downloadingPdfLeadId, setDownloadingPdfLeadId] = useState<
        number | null
    >(null)
    const [
        sendingScreeningLinkLeadId,
        setSendingScreeningLinkLeadId,
    ] = useState<number | null>(null)
    const [startingScreeningLeadId, setStartingScreeningLeadId] = useState<
        number | null
    >(null)
    const [resendingScreeningLeadId, setResendingScreeningLeadId] = useState<
        number | null
    >(null)
    const [sendingLeasePackageLeadId, setSendingLeasePackageLeadId] = useState<
        number | null
    >(null)
    const [
        signingAsPropertyManagerLeadId,
        setSigningAsPropertyManagerLeadId,
    ] = useState<number | null>(null)
    const [updatingTourLeadId, setUpdatingTourLeadId] = useState<number | null>(
        null,
    )
    const [highlightedLeadId, setHighlightedLeadId] = useState<number | null>(
        null,
    )

    const isSearchActive = query.trim().length > 0
    const lastAutoSwitchQueryRef = useRef('')
    const { openPersonFile } = usePersonFile()

    const [filters, setFilters] = useState<LeadsFilters>({
        is_lead: true,
        page: 1,
        page_size: 25,
        leasing_season_id: seasonId ?? undefined,
    })

    const pipelineCountsQuery = usePipelineCounts(seasonId, query)

    useEffect(() => {
        if (!pipelineTabs.some((tab) => tab.id === activeTabId)) {
            setActiveTabId(pipelineTabs[0].id)
        }
    }, [pipelineTabs, activeTabId])

    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            leasing_season_id: seasonId ?? undefined,
            pipeline_tab: resolvePipelineTabFilter(
                activeTabId,
                isSearchActive,
                leadsScope,
            ),
            pipeline_stage: undefined,
            lead_source:
                activeTabId === 'leads' && selectedSources.length > 0
                    ? selectedSources.join(',')
                    : undefined,
            application_status:
                activeTabId === 'app_sent' && applicationStatusFilter !== 'all'
                    ? applicationStatusFilter
                    : undefined,
            screening_status:
                activeTabId === 'screening' && screeningStatusFilter !== 'all'
                    ? screeningStatusFilter
                    : undefined,
            ordering:
                activeTabId === 'app_sent' ? 'application_activity' : undefined,
            page: 1,
        }))
    }, [
        seasonId,
        activeTabId,
        isSearchActive,
        leadsScope,
        selectedSources,
        applicationStatusFilter,
        screeningStatusFilter,
    ])

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((prev) => ({
                ...prev,
                search: query.trim() || undefined,
                page: 1,
            }))
        }, 300)
        return () => clearTimeout(timer)
    }, [query])

    useEffect(() => {
        if (!isSearchActive) {
            lastAutoSwitchQueryRef.current = ''
            setHighlightedLeadId(null)
        }
    }, [isSearchActive])

    const {
        leads,
        leadsPagination,
        isLoadingLeads,
        refetchLeads,
        syncLead,
        approveApplication,
        resendApplicationEmail,
        isApprovingApplication,
        sendScreeningLink,
        isSendingScreeningLink,
        sendLeasePackage,
        signLeasePackageAsProperty,
        startScreening,
        isStartingScreening,
        resendScreeningInvitation,
        isResendingScreeningInvitation,
    } = useLeads({
        apartmentId,
        filters,
        enabled: !!apartmentId && seasonId != null,
    })

    const { updateLeadTour } = useLeadTour()

    const rows = useMemo(
        () => leads.map((lead) => mapLeadToPipelineRecord(lead, pipelineTabs)),
        [leads, pipelineTabs],
    )

    const stageCounts = useMemo(
        () => remapPipelineCountsToDemoTabs(pipelineCountsQuery.data?.counts),
        [pipelineCountsQuery.data?.counts],
    )

    const searchMatches = useMemo(() => {
        if (!isSearchActive || !pipelineCountsQuery.data?.search_matches) {
            return null
        }
        return remapPipelineCountsToDemoTabs(
            pipelineCountsQuery.data.search_matches,
        )
    }, [isSearchActive, pipelineCountsQuery.data?.search_matches])

    const searchSummary = useMemo(() => {
        if (!isSearchActive || !leadsPagination) {
            return null
        }
        const total = leadsPagination.count
        if (total === 0) {
            return `No leads matching "${query.trim()}" in this season`
        }
        if (!searchMatches) {
            return `${total} result${total === 1 ? '' : 's'} across pipeline`
        }
        const parts = (Object.entries(searchMatches) as [string, number][])
            .filter(([, n]) => n > 0)
            .map(([tabId, n]) => {
                const label =
                    pipelineTabs.find((tab) => tab.id === tabId)?.label ??
                    PIPELINE_STAGE_LABEL[tabId as PipelineStage] ??
                    tabId
                return `${label} (${n})`
            })
        if (parts.length === 0) {
            return `${total} result${total === 1 ? '' : 's'} across pipeline`
        }
        return `Matches in ${parts.join(', ')}`
    }, [isSearchActive, leadsPagination, query, searchMatches, pipelineTabs])

    const activeTab =
        pipelineTabs.find((tab) => tab.id === activeTabId) ?? pipelineTabs[0]
    const columnStage = tabIdToColumnStage(activeTabId, activeTab)
    const isLeadsTab = activeTabId === 'leads'
    // Other tabs: show stage only while searching across the pipeline.
    const showStageColumn = isSearchActive && !isLeadsTab

    const inLeadsCount = isSearchActive
        ? searchMatches?.leads ?? stageCounts.leads ?? 0
        : stageCounts.leads ?? 0
    const everyoneCount = isSearchActive
        ? sumCounts(searchMatches) || (leadsPagination?.count ?? 0)
        : sumCounts(stageCounts)

    const sourceCounts = useMemo((): LeadSourceCounts => {
        const data = pipelineCountsQuery.data
        if (!data) {
            return EMPTY_SOURCE_COUNTS
        }
        if (isSearchActive) {
            return leadsScope === 'in_leads'
                ? data.search_source_counts_in_leads ?? EMPTY_SOURCE_COUNTS
                : data.search_source_counts ?? EMPTY_SOURCE_COUNTS
        }
        return leadsScope === 'in_leads'
            ? data.source_counts_in_leads ?? EMPTY_SOURCE_COUNTS
            : data.source_counts ?? EMPTY_SOURCE_COUNTS
    }, [pipelineCountsQuery.data, isSearchActive, leadsScope])

    const emptyMessage = isSearchActive
        ? `No leads matching "${query.trim()}" in this season.`
        : isLeadsTab && leadsScope === 'in_leads'
        ? 'No people currently in Leads.'
        : isLeadsTab
        ? 'No people in this season yet.'
        : 'No people in this stage.'

    // Single-match search: jump to the person's current stage and highlight.
    // Stay on Leads when using the roster (directory) so people are always findable there.
    useEffect(() => {
        const q = query.trim()
        if (!q || isLoadingLeads) {
            return
        }
        if (leadsPagination?.count !== 1 || leads.length !== 1) {
            return
        }
        if (lastAutoSwitchQueryRef.current === q) {
            return
        }

        setHighlightedLeadId(leads[0].id)
        if (!isLeadsTab) {
            const record = mapLeadToPipelineRecord(leads[0], pipelineTabs)
            if (record.tabId && activeTabId !== record.tabId) {
                setActiveTabId(record.tabId)
            }
        }
        lastAutoSwitchQueryRef.current = q
    }, [
        query,
        leads,
        leadsPagination,
        isLoadingLeads,
        activeTabId,
        pipelineTabs,
        isLeadsTab,
    ])

    const handleTabChange = (nextTabId: string) => {
        setActiveTabId(nextTabId)
        if (nextTabId === 'leads') {
            setLeadsScope('everyone')
            setSelectedSources([])
        }
        if (nextTabId !== 'app_sent') {
            setApplicationStatusFilter('all')
        }
        if (nextTabId !== 'screening') {
            setScreeningStatusFilter('all')
        }
    }

    const handleLeadsScopeChange = (scope: LeadsRosterScope) => {
        setLeadsScope(scope)
    }

    const handleRowClick = (record: PipelineRecord) => {
        if (record.leadId != null) {
            const lead = leads.find((l) => l.id === record.leadId) ?? null
            if (lead) {
                openPersonFile({ lead, leadId: lead.id })
            }
        }
    }

    const handlePageChange = (_: unknown, newPage: number) => {
        setFilters((prev) => ({ ...prev, page: newPage + 1 }))
    }

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setFilters((prev) => ({
            ...prev,
            page_size: parseInt(event.target.value, 10),
            page: 1,
        }))
    }

    const handleApproveApplication = useCallback(
        async (leadId: number) => {
            await approveApplication(leadId)
            refetchLeads()
        },
        [approveApplication, refetchLeads],
    )

    const handleRemindApplication = useCallback(
        async (leadId: number) => {
            setRemindingLeadId(leadId)
            try {
                await resendApplicationEmail(leadId)
                refetchLeads()
            } catch {
                // Toast handled in useLeads
            } finally {
                setRemindingLeadId(null)
            }
        },
        [resendApplicationEmail, refetchLeads],
    )

    const handleDownloadApplicationPdf = useCallback(async (leadId: number) => {
        setDownloadingPdfLeadId(leadId)
        try {
            const { data } = await axiosInstance.get<{
                download_url: string
            }>(`outreach/leads/${leadId}/application/submission-pdf/`)
            if (data.download_url) {
                window.open(data.download_url, '_blank', 'noopener,noreferrer')
            } else {
                toast.error('Submission PDF not available.')
            }
        } catch {
            toast.error('Unable to download application PDF.')
        } finally {
            setDownloadingPdfLeadId(null)
        }
    }, [])

    const handleSendScreeningLink = useCallback(
        async (leadId: number) => {
            setSendingScreeningLinkLeadId(leadId)
            try {
                await sendScreeningLink(leadId)
                refetchLeads()
            } catch {
                // Toast handled in useLeads
            } finally {
                setSendingScreeningLinkLeadId(null)
            }
        },
        [sendScreeningLink, refetchLeads],
    )

    const handleStartScreening = useCallback(
        async (leadId: number) => {
            setStartingScreeningLeadId(leadId)
            try {
                await startScreening(leadId)
                refetchLeads()
            } catch {
                // Toast handled in useLeads
            } finally {
                setStartingScreeningLeadId(null)
            }
        },
        [startScreening, refetchLeads],
    )

    const handleResendScreeningInvitation = useCallback(
        async (leadId: number) => {
            setResendingScreeningLeadId(leadId)
            try {
                const result = await resendScreeningInvitation(leadId)
                if (result.invitation_url) {
                    try {
                        await navigator.clipboard.writeText(
                            result.invitation_url,
                        )
                        toast.info('Invite URL copied to clipboard')
                    } catch {
                        // Clipboard may be unavailable; URL still shown in panel
                    }
                }
                refetchLeads()
            } catch {
                // Toast handled in useLeads
            } finally {
                setResendingScreeningLeadId(null)
            }
        },
        [resendScreeningInvitation, refetchLeads],
    )

    const handleSendLeasePackage = useCallback(
        async (leadId: number) => {
            setSendingLeasePackageLeadId(leadId)
            try {
                await sendLeasePackage(leadId)
                refetchLeads()
            } catch {
                // Toast handled in useLeads
            } finally {
                setSendingLeasePackageLeadId(null)
            }
        },
        [sendLeasePackage, refetchLeads],
    )

    const handleSignAsPropertyManager = useCallback(
        async (leadId: number) => {
            setSigningAsPropertyManagerLeadId(leadId)
            try {
                await signLeasePackageAsProperty(leadId)
                refetchLeads()
            } catch {
                // Toast handled in useLeads
            } finally {
                setSigningAsPropertyManagerLeadId(null)
            }
        },
        [signLeasePackageAsProperty, refetchLeads],
    )

    const handleOpenInviteDialog = useCallback(
        async (leadId: number) => {
            const lead = leads.find((l) => l.id === leadId) ?? null
            if (lead) {
                setInviteLead(lead)
            }
        },
        [leads],
    )

    const handleOpenScheduleTour = useCallback(
        (leadId: number, tourId?: number) => {
            const lead = leads.find((l) => l.id === leadId) ?? null
            if (lead) {
                setTourLead(lead)
                setEditTourId(tourId ?? null)
            }
        },
        [leads],
    )

    const handleCompleteTour = useCallback(
        async (leadId: number, tourId: number) => {
            setUpdatingTourLeadId(leadId)
            try {
                await updateLeadTour({
                    leadId,
                    tourId,
                    payload: { status: 'completed' },
                })
                refetchLeads()
            } catch {
                // Toast in useLeadTour
            } finally {
                setUpdatingTourLeadId(null)
            }
        },
        [updateLeadTour, refetchLeads],
    )

    const handleNoShowTour = useCallback(
        async (leadId: number, tourId: number) => {
            setUpdatingTourLeadId(leadId)
            try {
                await updateLeadTour({
                    leadId,
                    tourId,
                    payload: { status: 'no_show' },
                })
                refetchLeads()
            } catch {
                // Toast in useLeadTour
            } finally {
                setUpdatingTourLeadId(null)
            }
        },
        [updateLeadTour, refetchLeads],
    )

    if (seasonId == null) {
        return null
    }

    return (
        <div className={classes.root}>
            <div className={classes.stickyChrome}>
                <StageSwitcher
                    tabs={pipelineTabs}
                    activeTabId={activeTabId}
                    counts={stageCounts}
                    searchMatches={searchMatches}
                    isSearchActive={isSearchActive}
                    onTabChange={handleTabChange}
                />
                {isLeadsTab ? (
                    <LeadsRosterHeader
                        scope={leadsScope}
                        onScopeChange={handleLeadsScopeChange}
                        inLeadsCount={inLeadsCount}
                        everyoneCount={everyoneCount}
                        query={query}
                        onQueryChange={setQuery}
                        selectedSources={selectedSources}
                        onSourcesChange={setSelectedSources}
                        sourceCounts={sourceCounts}
                        sourceCountsLoading={pipelineCountsQuery.isLoading}
                        showingCount={leadsPagination?.count ?? rows.length}
                        showAddLead={!readOnly}
                        onAddLead={() => setAddLeadOpen(true)}
                    />
                ) : (
                    <PipelineToolbar
                        query={query}
                        onQueryChange={setQuery}
                        rowCount={leadsPagination?.count ?? rows.length}
                        searchSummary={searchSummary}
                        showAddLead={false}
                        applicationStatusFilter={
                            activeTabId === 'app_sent'
                                ? applicationStatusFilter
                                : undefined
                        }
                        onApplicationStatusFilterChange={
                            activeTabId === 'app_sent'
                                ? setApplicationStatusFilter
                                : undefined
                        }
                        screeningStatusFilter={
                            activeTabId === 'screening'
                                ? screeningStatusFilter
                                : undefined
                        }
                        onScreeningStatusFilterChange={
                            activeTabId === 'screening'
                                ? setScreeningStatusFilter
                                : undefined
                        }
                    />
                )}
            </div>

            <div className={classes.tableSlot}>
                <StageTable
                    stage={columnStage}
                    rows={rows}
                    isLoading={isLoadingLeads}
                    showStageColumn={showStageColumn}
                    leadsRosterLayout={isLeadsTab}
                    tabLabelsById={Object.fromEntries(
                        pipelineTabs.map((tab) => [tab.id, tab.label]),
                    )}
                    highlightedLeadId={highlightedLeadId}
                    emptyMessage={emptyMessage}
                    onRowClick={handleRowClick}
                    onRemindApplication={
                        !readOnly && activeTabId === 'app_sent'
                            ? handleRemindApplication
                            : undefined
                    }
                    onDownloadApplicationPdf={
                        activeTabId === 'app_sent'
                            ? handleDownloadApplicationPdf
                            : undefined
                    }
                    onInviteToApply={
                        !readOnly && isLeadsTab
                            ? handleOpenInviteDialog
                            : undefined
                    }
                    onSendScreeningLink={
                        !readOnly && activeTabId === 'app_sent'
                            ? handleSendScreeningLink
                            : undefined
                    }
                    onStartScreening={
                        !readOnly && columnStage === 'screening'
                            ? handleStartScreening
                            : undefined
                    }
                    onResendScreening={
                        !readOnly && columnStage === 'screening'
                            ? handleResendScreeningInvitation
                            : undefined
                    }
                    onSendLeasePackage={
                        !readOnly &&
                        (columnStage === 'leases' ||
                            columnStage === 'lease_sent')
                            ? handleSendLeasePackage
                            : undefined
                    }
                    onSendLeaseOffer={
                        !readOnly && columnStage === 'screening'
                            ? setOfferRecord
                            : undefined
                    }
                    onSignAsPropertyManager={
                        !readOnly &&
                        (columnStage === 'leases' ||
                            columnStage === 'lease_sent')
                            ? handleSignAsPropertyManager
                            : undefined
                    }
                    onScheduleTour={
                        !readOnly &&
                        (activeTabId === 'leads' || columnStage === 'tours')
                            ? handleOpenScheduleTour
                            : undefined
                    }
                    onCompleteTour={
                        !readOnly &&
                        (activeTabId === 'leads' || columnStage === 'tours')
                            ? handleCompleteTour
                            : undefined
                    }
                    onNoShowTour={
                        !readOnly &&
                        (activeTabId === 'leads' || columnStage === 'tours')
                            ? handleNoShowTour
                            : undefined
                    }
                    remindingLeadId={remindingLeadId}
                    downloadingPdfLeadId={downloadingPdfLeadId}
                    sendingScreeningLinkLeadId={sendingScreeningLinkLeadId}
                    startingScreeningLeadId={startingScreeningLeadId}
                    resendingScreeningLeadId={resendingScreeningLeadId}
                    sendingLeasePackageLeadId={sendingLeasePackageLeadId}
                    signingAsPropertyManagerLeadId={
                        signingAsPropertyManagerLeadId
                    }
                    updatingTourLeadId={updatingTourLeadId}
                />
            </div>

            <div className={classes.paginationSlot}>
                {leadsPagination && leadsPagination.count > 0 ? (
                    <TablePagination
                        component="div"
                        count={leadsPagination.count}
                        page={Math.max(0, (filters.page ?? 1) - 1)}
                        onChangePage={handlePageChange}
                        rowsPerPage={filters.page_size ?? 25}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50]}
                    />
                ) : null}
            </div>

            <InviteToApplyDialog
                open={inviteLead != null}
                lead={inviteLead}
                onClose={() => setInviteLead(null)}
                onSuccess={() => {
                    refetchLeads()
                    pipelineCountsQuery.refetch()
                }}
            />

            <ScheduleTourDialog
                open={tourLead != null}
                lead={tourLead}
                tourId={editTourId}
                onClose={() => {
                    setTourLead(null)
                    setEditTourId(null)
                }}
                onSuccess={() => {
                    refetchLeads()
                    pipelineCountsQuery.refetch()
                }}
            />

            <SendLeaseOfferDialog
                open={offerRecord != null}
                record={offerRecord}
                leasingSeasonId={seasonId ?? undefined}
                onClose={() => setOfferRecord(null)}
                onSent={() => {
                    refetchLeads()
                    pipelineCountsQuery.refetch()
                }}
            />

            {apartmentId != null && (
                <AddWalkInLeadDialog
                    open={addLeadOpen}
                    onClose={() => setAddLeadOpen(false)}
                    seasonId={seasonId}
                    apartmentId={apartmentId}
                    onSuccess={() => {
                        refetchLeads()
                        pipelineCountsQuery.refetch()
                    }}
                />
            )}
        </div>
    )
}
